import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { adminSectionDefinition, adminSectionNavLabel } from '../shared/adminSectionRegistry';
import { matchesQuickSearch, QuickSearchInput } from '../shared/quickSearch';
import { AdminCreateCard } from '../shared/AdminCreateCard';
import { Drawer, EmployeeDossierDrawer } from './hrModule';
import { MultiSelectDropdown } from '../shared/MultiSelectDropdown';

/**
 * Modul 126 (osobe zaduženja) ⇄ modul 101 (korisnici) spojeni: osoba zaduženja = korisnik.
 * Tvrtka i odjel dolaze iz korisnika; ERP "ID" (erp_bi_user_id) i MTP pune se iz ERP-a.
 */
type Row = {
    id: number;
    company_id: number | null;
    display_name: string;
    email: string | null;
    status: string;
    role: string;
    is_protected: boolean;
    erp_bi_user_id: string | null;
    default_department_id: number | null;
    default_cost_place_node_id: number | null;
    deleted_at?: string | null;
    employee_id: number | null;
};

type Dept = { id: number; company_id: number; code: string; name: string };
type MtpNode = { id: number; company_id: number; code: string; name_hr: string; name_en: string | null; allows_posting: boolean };

type Payload = {
    rows: Row[];
    companies: Array<{ id: number; code: string; name: string }>;
    departments?: Dept[];
    mtp_nodes?: MtpNode[];
};

export function AdminE2PersonsSection({
    token,
    locale,
    reloadTick,
}: {
    token: string;
    locale: 'hr' | 'en';
    reloadTick: number;
}) {
    const def = adminSectionDefinition('126');
    const title = def ? adminSectionNavLabel(def, locale) : '126';
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [payload, setPayload] = useState<Payload | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [tableSearch, setTableSearch] = useState('');
    const [companyFilter, setCompanyFilter] = useState<Array<number | string>>([]);
    const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');

    const [nCompanyId, setNCompanyId] = useState<string>('');
    const [nDisplayName, setNDisplayName] = useState('');
    const [nErpId, setNErpId] = useState('');
    const [nDeptId, setNDeptId] = useState('');
    const [nMtpId, setNMtpId] = useState('');

    const [editId, setEditId] = useState<number | null>(null);
    const [empDrawer, setEmpDrawer] = useState<number | null>(null); // HR drawer (isti set podataka kao modul 60)
    const [eDisplayName, setEDisplayName] = useState('');
    const [eErpId, setEErpId] = useState('');
    const [eDeptId, setEDeptId] = useState('');
    const [eMtpId, setEMtpId] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api<Payload>('/admin/e2/persons', token);
            setPayload(res);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
            setPayload(null);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        void load();
    }, [load, reloadTick]);

    const rows = payload?.rows ?? [];
    const departments = payload?.departments ?? [];
    const mtpNodes = payload?.mtp_nodes ?? [];

    const deptById = useMemo(() => new Map(departments.map((d) => [Number(d.id), d])), [departments]);
    const mtpById = useMemo(() => new Map(mtpNodes.map((n) => [Number(n.id), n])), [mtpNodes]);
    const companyById = useMemo(
        () => new Map((payload?.companies ?? []).map((c) => [Number(c.id), c])),
        [payload?.companies],
    );

    const filteredRows = useMemo(() => {
        const allow = new Set(companyFilter.map((v) => Number(v)));
        return rows.filter((r) => {
            if (allow.size > 0 && !allow.has(Number(r.company_id))) return false;
            if (statusFilter === 'active' && r.deleted_at) return false;
            if (statusFilter === 'archived' && !r.deleted_at) return false;
            return matchesQuickSearch(
                [
                    r.id,
                    r.display_name,
                    r.email,
                    r.erp_bi_user_id,
                    r.status,
                    deptById.get(Number(r.default_department_id))?.name,
                    mtpById.get(Number(r.default_cost_place_node_id))?.code,
                    companyById.get(Number(r.company_id))?.code,
                    companyById.get(Number(r.company_id))?.name,
                ],
                tableSearch,
            );
        });
    }, [rows, tableSearch, companyFilter, statusFilter, deptById, mtpById, companyById]);

    async function submitNew(ev: FormEvent) {
        ev.preventDefault();
        if (!nDisplayName.trim()) return;
        try {
            await mutate('/admin/e2/persons', token, 'POST', {
                display_name: nDisplayName.trim(),
                erp_bi_user_id: nErpId.trim() || null,
                primary_department_id: nDeptId.trim() ? Number(nDeptId) : null,
                mtp_cost_place_node_id: nMtpId.trim() ? Number(nMtpId) : null,
            });
            setNCompanyId('');
            setNDisplayName('');
            setNErpId('');
            setNDeptId('');
            setNMtpId('');
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
        }
    }

    function startEdit(row: Row) {
        setEditId(row.id);
        setEDisplayName(row.display_name);
        setEErpId(row.erp_bi_user_id ?? '');
        setEDeptId(row.default_department_id != null ? String(row.default_department_id) : '');
        setEMtpId(row.default_cost_place_node_id != null ? String(row.default_cost_place_node_id) : '');
    }

    async function saveEdit(ev: FormEvent) {
        ev.preventDefault();
        if (!editId) return;
        try {
            await mutate(`/admin/e2/persons/${editId}`, token, 'PATCH', {
                display_name: eDisplayName.trim(),
                erp_bi_user_id: eErpId.trim() || null,
                primary_department_id: eDeptId.trim() ? Number(eDeptId) : null,
                mtp_cost_place_node_id: eMtpId.trim() ? Number(eMtpId) : null,
            });
            setEditId(null);
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
        }
    }

    async function archiveRow(id: number) {
        try {
            await mutate(`/admin/e2/persons/${id}/archive`, token, 'PATCH', {});
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
        }
    }

    const statusLabel = (r: Row): string => {
        if (r.deleted_at) return t('Arhivirana', 'Archived');
        if (r.status === 'pending') return t('Na čekanju', 'Pending');
        if (r.status === 'inactive') return t('Neaktivna', 'Inactive');
        return t('Aktivna', 'Active');
    };

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">👥</div>
                <div className="bi-module-hero-text">
                    <h1>{title}</h1>
                    <p className="muted">
                        {t(
                            'Osoba zaduženja = korisnik (spojeno s modulom 101). Tvrtka i odjel dolaze iz korisnika; ERP ID i MTP pune se iz ERP-a.',
                            'Responsible person = user (merged with module 101). Company & department come from the user; ERP ID and MTP are populated from the ERP.',
                        )}
                    </p>
                </div>
            </div>
            {error ? <p className="error">{error}</p> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
            {!loading && payload ? (
                <>
                    <AdminCreateCard
                        title={t('Nova osoba zaduženja', 'New responsible person')}
                        hint={t(
                            'Kreira novog korisnika u statusu "na čekanju" (bez logina dok ne postavi email/lozinku). Odjel određuje tvrtku.',
                            'Creates a new user in "pending" status (no login until email/password set). Department determines the company.',
                        )}
                        locale={locale}
                    >
                        <form onSubmit={submitNew}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '0.75rem', maxWidth: '58rem' }}>
                                <label className="filter-label">
                                    {t('Ime i prezime / uloga', 'Name / role')}
                                    <input value={nDisplayName} onChange={(ev) => setNDisplayName(ev.target.value)} required />
                                </label>
                                <label className="filter-label">
                                    {t('ERP BI user ID', 'ERP BI user ID')}
                                    <input value={nErpId} onChange={(ev) => setNErpId(ev.target.value)} placeholder={t('iz ERP-a', 'from ERP')} />
                                </label>
                                <label className="filter-label">
                                    {t('Tvrtka (filtrira odjele)', 'Company (filters departments)')}
                                    <select value={nCompanyId} onChange={(ev) => { setNCompanyId(ev.target.value); setNDeptId(''); setNMtpId(''); }}>
                                        <option value="">{t('Sve', 'All')}</option>
                                        {payload.companies.map((c) => (
                                            <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="filter-label">
                                    {t('Odjel', 'Department')}
                                    <select value={nDeptId} onChange={(ev) => setNDeptId(ev.target.value)}>
                                        <option value="">{t('— Bez —', '— None —')}</option>
                                        {departments
                                            .filter((d) => !nCompanyId || Number(d.company_id) === Number(nCompanyId))
                                            .map((d) => (
                                                <option key={d.id} value={d.id}>{d.code ? d.code + ' — ' : ''}{d.name}</option>
                                            ))}
                                    </select>
                                </label>
                                <label className="filter-label">
                                    {t('MTP mjesto troška', 'MTP cost place')}
                                    <select value={nMtpId} onChange={(ev) => setNMtpId(ev.target.value)}>
                                        <option value="">{t('— Bez —', '— None —')}</option>
                                        {mtpNodes
                                            .filter((n) => !nCompanyId || Number(n.company_id) === Number(nCompanyId))
                                            .map((n) => (
                                                <option key={n.id} value={n.id}>{n.code} — {n.name_hr}{n.allows_posting ? '' : ' [grupa]'}</option>
                                            ))}
                                    </select>
                                </label>
                            </div>
                            <div className="admin-toolbar" style={{ marginTop: '0.75rem' }}>
                                <button type="submit" className="compact">{t('Kreiraj', 'Create')}</button>
                            </div>
                        </form>
                    </AdminCreateCard>

                    <div className="admin-toolbar" style={{ marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                        <span className="muted">{t('Status', 'Status')}:</span>
                        {(['active', 'archived', 'all'] as const).map((s) => (
                            <button
                                key={s}
                                type="button"
                                className={statusFilter === s ? 'compact' : 'secondary compact'}
                                onClick={() => setStatusFilter(s)}
                            >
                                {s}
                            </button>
                        ))}
                        <span className="muted" style={{ marginLeft: '0.75rem' }}>{t('Tvrtka', 'Company')}:</span>
                        <div style={{ minWidth: '14rem' }}>
                            <MultiSelectDropdown
                                locale={locale}
                                options={payload.companies.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                                selected={companyFilter}
                                onChange={setCompanyFilter}
                                emptyLabel={t('Sve tvrtke', 'All companies')}
                            />
                        </div>
                        <QuickSearchInput
                            value={tableSearch}
                            onChange={setTableSearch}
                            placeholder={t('Pretraga osoba…', 'Search persons…')}
                        />
                    </div>
                    <div className="table-wrap">
                        <table className="admin-user-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>{t('Tvrtka', 'Company')}</th>
                                    <th>{t('Ime / uloga', 'Name / role')}</th>
                                    <th>{t('ERP BI user', 'ERP BI user')}</th>
                                    <th>{t('Odjel', 'Dept.')}</th>
                                    <th>MTP</th>
                                    <th>{t('Status', 'Status')}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map((r) => {
                                    const co = r.company_id != null ? companyById.get(Number(r.company_id)) : null;
                                    const dept = r.default_department_id != null ? deptById.get(Number(r.default_department_id)) : null;
                                    const mtp = r.default_cost_place_node_id != null ? mtpById.get(Number(r.default_cost_place_node_id)) : null;
                                    return (
                                        <tr key={r.id} style={r.deleted_at ? { opacity: 0.55 } : (r.status === 'pending' ? { background: 'rgba(var(--ad-acc-rgb),0.06)' } : undefined)}>
                                            <td>{r.id}</td>
                                            <td>{co ? `${co.code} — ${co.name}` : <span className="muted">—</span>}</td>
                                            <td>
                                                {r.display_name}
                                                {r.email ? <span className="muted" style={{ display: 'block', fontSize: '0.78rem' }}>{r.email}</span> : null}
                                            </td>
                                            <td>{r.erp_bi_user_id ?? <span className="muted">—</span>}</td>
                                            <td>{dept ? `${dept.code ? dept.code + ' — ' : ''}${dept.name}` : <span className="muted">—</span>}</td>
                                            <td>{mtp ? `${mtp.code} — ${mtp.name_hr}` : <span className="muted">—</span>}</td>
                                            <td>{statusLabel(r)}</td>
                                            <td className="admin-toolbar">
                                                <button type="button" className="secondary compact" onClick={() => r.employee_id ? setEmpDrawer(r.employee_id) : startEdit(r)}>{t('Uredi', 'Edit')}</button>
                                                <button
                                                    type="button"
                                                    className="secondary compact"
                                                    disabled={r.is_protected}
                                                    onClick={() => void archiveRow(r.id)}
                                                >
                                                    {r.deleted_at ? t('Vrati', 'Restore') : t('Arhiviraj', 'Archive')}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredRows.length === 0 ? (
                            <p className="muted">{t('Nema zapisa.', 'No rows.')}</p>
                        ) : null}
                    </div>

                    {editId ? (
                        <form className="admin-section-card" style={{ marginTop: '1rem' }} onSubmit={saveEdit}>
                            <h4>{t(`Uredi osobu #${editId}`, `Edit person #${editId}`)}</h4>
                            <label className="filter-label">
                                {t('Ime i prezime / uloga', 'Name / role')}
                                <input value={eDisplayName} onChange={(ev) => setEDisplayName(ev.target.value)} required />
                            </label>
                            <label className="filter-label">
                                {t('ERP BI user ID', 'ERP BI user ID')}
                                <input value={eErpId} onChange={(ev) => setEErpId(ev.target.value)} placeholder={t('iz ERP-a', 'from ERP')} />
                            </label>
                            <label className="filter-label">
                                {t('Odjel', 'Department')}
                                <select value={eDeptId} onChange={(ev) => setEDeptId(ev.target.value)}>
                                    <option value="">{t('— Bez —', '— None —')}</option>
                                    {departments.map((d) => (
                                        <option key={d.id} value={d.id}>{d.code ? d.code + ' — ' : ''}{d.name}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="filter-label">
                                {t('MTP mjesto troška', 'MTP cost place')}
                                <select value={eMtpId} onChange={(ev) => setEMtpId(ev.target.value)}>
                                    <option value="">{t('— Bez —', '— None —')}</option>
                                    {mtpNodes.map((n) => (
                                        <option key={n.id} value={n.id}>{n.code} — {n.name_hr}{n.allows_posting ? '' : ' [grupa]'}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="admin-toolbar">
                                <button type="submit" className="compact">{t('Spremi', 'Save')}</button>
                                <button type="button" className="secondary compact" onClick={() => setEditId(null)}>{t('Odustani', 'Cancel')}</button>
                            </div>
                        </form>
                    ) : null}
                </>
            ) : null}

            {empDrawer !== null ? (
                <Drawer onClose={() => setEmpDrawer(null)}>
                    <EmployeeDossierDrawer token={token} locale={locale} employeeId={empDrawer} onClose={() => setEmpDrawer(null)} onChanged={() => void load()} />
                </Drawer>
            ) : null}
        </section>
    );
}
